// doctor-frontend/src/pages/intern/components/GuidelineSearchBar.js
// Compact search + filter bar for the Full Guided Activity Catalogue.
// Controlled component: parent owns query/categories/tags.
// No-regression refinement:
// - Preserves debounced search, multi-category filters, multi-tag filters, and Clear behavior.
// - Replaces long always-visible category/tag chip walls with compact add-filter selectors.
// - Shows only active filters as removable chips, preventing malformed/narrow controls on long training-area names.
// - Keeps accessibility labels and keyboard-friendly native selectors.

import React, { useEffect, useMemo, useRef, useState } from 'react';

export default function GuidelineSearchBar({
  id = 'guideline-search',
  query, setQuery,
  categories, setCategories,
  tags, setTags,
  allCategories = [],
  allTags = [],
  onClear = () => {}
}) {
  const [typed, setTyped] = useState(query || '');
  const timer = useRef(null);

  useEffect(() => {
    setTyped(query || '');
  }, [query]);

  // Debounce typing → setQuery
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setQuery(typed), 250);
    return () => clearTimeout(timer.current);
  }, [typed, setQuery]);

  const remainingCategories = useMemo(
    () => allCategories.filter((c) => !categories.includes(c)),
    [allCategories, categories]
  );

  const remainingTags = useMemo(
    () => allTags.filter((t) => !tags.includes(t)),
    [allTags, tags]
  );

  const addCategory = (value) => {
    const c = String(value || '').trim();
    if (c && !categories.includes(c)) setCategories([...categories, c]);
  };

  const removeCategory = (c) => setCategories(categories.filter((x) => x !== c));

  const addTag = (value) => {
    const t = String(value || '').trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
  };

  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const hasFilters = Boolean(categories.length || tags.length || typed);

  return (
    <div className="rounded-2xl border p-3 shadow-sm bg-white dark:bg-neutral-900 dark:border-neutral-800">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(260px,1fr)_minmax(220px,320px)_minmax(200px,280px)_auto] gap-2.5 items-center">
        <input
          id={id}
          type="search"
          placeholder="Search activities, competencies, skills, codes or tags…"
          aria-label="Search guided activities, competencies and skills"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          className="w-full min-w-0 px-3 py-2.5 rounded-lg border bg-transparent dark:border-neutral-700"
        />

        <select
          aria-label="Add training area filter"
          value=""
          onChange={(e) => addCategory(e.target.value)}
          className="w-full min-w-0 px-3 py-2.5 rounded-lg border bg-white dark:bg-neutral-900 dark:border-neutral-700"
        >
          <option value="">Filter by training area…</option>
          {remainingCategories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <select
          aria-label="Add tag filter"
          value=""
          onChange={(e) => addTag(e.target.value)}
          className="w-full min-w-0 px-3 py-2.5 rounded-lg border bg-white dark:bg-neutral-900 dark:border-neutral-700"
        >
          <option value="">Add tag filter…</option>
          {remainingTags.map((t) => (
            <option key={t} value={t}>#{t}</option>
          ))}
        </select>

        <button
          type="button"
          disabled={!hasFilters}
          onClick={() => {
            setTyped('');
            onClear();
          }}
          className={
            hasFilters
              ? 'px-4 py-2.5 rounded-lg text-sm font-medium border whitespace-nowrap hover:bg-neutral-50 dark:hover:bg-neutral-800'
              : 'px-4 py-2.5 rounded-lg text-sm font-medium border whitespace-nowrap opacity-40 cursor-not-allowed'
          }
        >
          Clear filters
        </button>
      </div>

      {(categories.length > 0 || tags.length > 0) ? (
        <div className="mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-800">
          <div className="text-xs opacity-65 mb-2">Active filters</div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <span
                key={`category-${c}`}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full bg-neutral-100 dark:bg-neutral-800"
              >
                {c}
                <button
                  type="button"
                  aria-label={`Remove training area filter ${c}`}
                  onClick={() => removeCategory(c)}
                  className="ml-1 opacity-60 hover:opacity-100 font-bold"
                >
                  ×
                </button>
              </span>
            ))}

            {tags.map((t) => (
              <span
                key={`tag-${t}`}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full bg-neutral-100 dark:bg-neutral-800"
              >
                #{t}
                <button
                  type="button"
                  aria-label={`Remove tag filter ${t}`}
                  onClick={() => removeTag(t)}
                  className="ml-1 opacity-60 hover:opacity-100 font-bold"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
